import { useEffect, useState } from "react";
import { Col, Collapse, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCalculateSolarEstimationCostMutation,
  usePurchaseSolarEstimationMutation,
  useSubmitForApprovalMutation,
} from "../../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectAuth } from "../../../../redux/features/auth/auth-slice";
import CalculateSolarEstimationCostRequestDTO from "../../../../shared/oversight-core/dtos/request-dtos/calculate-solar-estimation-cost-request-dto";
import SubmitForApprovalRequestDTO from "../../../../shared/oversight-core/dtos/request-dtos/submit-for-approval-request-dto";
import CalculateSolarEstimationCostResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/calculate-solar-estimation-cost-response-dto";
import {
  IViewSolarEstimationData,
  IViewSolarUserSelectedProducts,
} from "../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { EApplicationType } from "../../../../shared/oversight-core/enums/application-type";
import { ECountry } from "../../../../shared/oversight-core/enums/country";
import { EEstimationType } from "../../../../shared/oversight-core/enums/estimation-type";
import { EInverterPhaseType } from "../../../../shared/oversight-core/enums/inverter-phase-type";
import { OvstErrorCode } from "../../../../shared/oversight-core/enums/ovst-error-codes";
import { ESolarSolutionType } from "../../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSimLocalStorage } from "../../../../shared/oversight-core/enums/solar-sim-local-storage";
import { ESolarSystemType } from "../../../../shared/oversight-core/enums/solar-system-type ";
import { EUtilityOptionType } from "../../../../shared/oversight-core/enums/utility-option-type";
import { EViewSolarEstimationSolarProductCategory } from "../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import { AppRoute } from "../../../../shared/oversight-core/interfaces/app-routes";
import { IEstimatorDetails } from "../../../../shared/oversight-core/interfaces/estimator-details";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import { IViewSolarEstimationSolarProductAndOtherDetails } from "../../../../shared/oversight-core/interfaces/view-solar-estimation-solar-system";
import AppSwitch from "../../../../shared/oversight-core/ui-elements/app-switch/app-switch";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import { formatWithCommas } from "../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";
import UpdateHistoryModal from "../../../solar-sim-dashboard/estimations/components/update-history-modal/update-history-modal";
import ApprovedSolarComponent from "../approved-solar-component/approved-solar-component";
import { ISimplifiedDevice } from "../simplified-device-energy-card/simplified-device-energy-card";
import SubmitForApprovalLoginModal from "../submit-for-approval-login-modal/submit-for-approval-login-modal";
import SubmitForExpertApprovalModal from "../submit-for-expert-approval-modal/submit-for-expert-approval-modal";
import SystemPurchaseModal from "../system-purchase-modal/system-purchase-modal";
import styles from "./approved-system.module.scss";

interface IProps {
  userSelectedProducts: IViewSolarUserSelectedProducts[];
  assembleSystemData: IViewSolarEstimationData | undefined;
  onSave: () => void;
  currency: string;
  estimatorDetails: IEstimatorDetails;
  includeOtherFees: boolean;
  setIncludeOtherFees: React.Dispatch<React.SetStateAction<boolean>>;
  comment: string;
  devices?: ISimplifiedDevice[];
  title: string;
  estimationType: ESolarSolutionType;
  applicationType: EApplicationType;
  utilityOptionType: EUtilityOptionType;
  country: ECountry;
  monthlyRequiredEnergy: number;
  maxRequiredPower: number;
  numberOfUsers: number;
  nightLoad: number;
  systemType: ESolarSystemType;
  inverterPhaseType: EInverterPhaseType;
  sunlightHours: number;
  estimationSpaceClusterId: string | null;
  estimationNo: number;
}

export interface IDataToSubmitForApproval {
  estimatorDetails: IEstimatorDetails;
  title: string;
  estimationType: ESolarSolutionType;
  applicationType: EApplicationType | null;
  utilityOptionType: EUtilityOptionType | null;
  solarEstimationType: EEstimationType | null;
  systemType: ESolarSystemType;
  inverterPhaseType: EInverterPhaseType;
  country: ECountry;
  monthlyRequiredEnergy: number;
  maxRequiredPower: number;
  numberOfUsers: number | null;
  nightLoad: number;
  sunlightHours: number;
  estimationSpaceClusterId: string | null;
  devices: ISimplifiedDevice[];
  address: string;
  note: string;
  includeOtherFees: boolean;
  longitude: number;
  latitude: number;
}

const ApprovedSystem = (props: IProps) => {
  const {
    userSelectedProducts,
    assembleSystemData,
    currency,
    estimatorDetails,
    includeOtherFees,
    comment,
    devices = [],
    title,
    estimationType,
    applicationType,
    utilityOptionType,
    country,
    monthlyRequiredEnergy,
    maxRequiredPower,
    numberOfUsers,
    nightLoad,
    systemType,
    inverterPhaseType,
    sunlightHours,
    estimationSpaceClusterId,
    setIncludeOtherFees,
    onSave,
    estimationNo,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const auth = useSelector(selectAuth);

  const [collapsed, setCollapsed] = useState(false);
  const [waterHeaterCollapsed, setWaterHeaterCollapsed] = useState(false);
  const [componentCost, setComponentCost] = useState(0);
  const [installationCost, setInstallationCost] = useState(0);
  const [accessoriesCost, setAccessoriesCost] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [isShowUpdateHistoryModal, setIsShowUpdateHistoryModal] =
    useState(false);
  const [
    isShowSubmitForApprovalLoginModal,
    setIsShowSubmitForApprovalLoginModal,
  ] = useState(false);
  const [
    isShowSubmitForExpertApprovalModal,
    setIsShowSubmitForExpertApprovalModal,
  ] = useState(false);
  const [longitude, setLongitude] = useState(0);
  const [latitude, setLatitude] = useState(0);
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [isShowSystemPurchaseModal, setIsShowSystemPurchaseModal] =
    useState(false);

  const [submitForApproval, { isLoading: isSubmittingForApproval }] =
    useSubmitForApprovalMutation();
  const [
    triggerCalculateSolarEstimationCost,
    { isLoading: isLoadingCalculateSolarEstimationCost },
  ] = useCalculateSolarEstimationCostMutation();
  const [
    triggerPurchaseSolarEstimation,
    { isLoading: isLoadingPurchaseSolarEstimation },
  ] = usePurchaseSolarEstimationMutation();

  useEffect(() => {
    const requestBody: CalculateSolarEstimationCostRequestDTO = {
      estimationId: auth.token ? estimatorDetails.estimationId : "",
      estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
      installationCostRequired: includeOtherFees,
    };

    triggerCalculateSolarEstimationCost({ ...requestBody })
      .unwrap()
      .then((res: CalculateSolarEstimationCostResponseDTO) => {
        setComponentCost(res.equipmentCost);
        setTotalCost(res.grandTotal);
        if (includeOtherFees) {
          setInstallationCost(res.totalLaborCost);
          setAccessoriesCost(res.totalAccessoriesCost);
        }
      })
      .catch(() => {
        showErrorMessage("Address is invalid. Please enter correct address");
      });
  }, [triggerCalculateSolarEstimationCost, includeOtherFees]);

  const onSubmitForApproval = (
    address: string,
    longitude: number,
    latitude: number,
    note: string,
  ) => {
    if (!estimatorDetails.estimationId || !estimatorDetails.estimationToken) {
      showErrorMessage("Failed to submit for approval");
      return;
    }

    const requestBody: SubmitForApprovalRequestDTO = {
      estimationId: auth.token ? estimatorDetails.estimationId : "",
      estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
      includeInstallationCost: includeOtherFees,
      longitude,
      latitude,
      address,
      note,
    };

    submitForApproval({ ...requestBody })
      .unwrap()
      .then(() => {
        if (pathname.includes("advanced")) {
          localStorage.removeItem(
            ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
          );
        }

        if (pathname.includes("simplified")) {
          localStorage.removeItem(
            ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
          );
          localStorage.removeItem("simplifiedSave");
        }

        if (pathname.includes("direct")) {
          localStorage.removeItem(
            ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
          );
          localStorage.removeItem("directSave");
        }

        if (pathname.includes("water-heater")) {
          localStorage.removeItem(
            ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
          );
          localStorage.removeItem("waterHeaterSave");
        }

        showSuccessMessage("Submitted for approval successfully");
        if (auth.token) {
          navigate(AppRoute.ESTIMATIONS);
          return;
        }

        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM);
      })
      .catch(() => {
        showErrorMessage("Failed to submit for approval");
      });
  };

  const onPurchaseSystem = (estimationId: string) => {
    // const base = "https://priyanthab5.wixstudio.com/shop";
    // const cart = userSelectedProducts.map((usp) => ({
    //   id: usp.id,
    //   count: usp.count,
    // }));

    // const url = new URL(base);

    // url.searchParams.set(
    //   "products",
    //   cart.map((item) => `${item.id}:${item.count}`).join(",")
    // );

    // const finalUrl = url.toString();

    // window.open(finalUrl, "_blank", "noopener,noreferrer");

    // if (pathname.includes("advanced")) {
    //   localStorage.removeItem(ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED);
    // }

    // if (pathname.includes("simplified")) {
    //   localStorage.removeItem(
    //     ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED
    //   );
    //   localStorage.removeItem("simplifiedSave");
    // }

    triggerPurchaseSolarEstimation({ estimationId })
      .unwrap()
      .then(() => {
        showSuccessMessage("System purchased successfully");
        navigate(AppRoute.ESTIMATIONS);
      })
      .catch((error: IHttpError) => {
        if (
          error.status === 412 &&
          error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
        ) {
          showErrorMessage("Solar estimation is yet to be reviewed.");
          return;
        }
        showErrorMessage("Failed to purchase system");
      });
  };

  return (
    <>
      <div className="px-4 py-3 rounded-3">
        {estimationType !== ESolarSolutionType.WATER_HEATING_SYSTEM && (
          <>
            <Row>
              <Col className="text-secondary font-size-14 font-weight-500">
                Solar Power Equipments
              </Col>
              <Col className="col-auto">
                <MaterialIcon
                  icon={"keyboard_arrow_up"}
                  size={18}
                  color="var(--color-dropdown-more-icon)"
                  className={`${styles.iconToggle} ${
                    collapsed ? styles.isCollapsed : ""
                  } cursor-pointer`}
                  onClick={() => {
                    setCollapsed((ps) => !ps);
                  }}
                />
              </Col>
            </Row>
            <Collapse in={!collapsed}>
              <div>
                {assembleSystemData &&
                  userSelectedProducts.map((usp, index) => {
                    let productDetails:
                      | IViewSolarEstimationSolarProductAndOtherDetails
                      | undefined;
                    const recommendedProducts =
                      assembleSystemData.recommendedProducts;

                    switch (usp.category) {
                      case EViewSolarEstimationSolarProductCategory.INVERTERS:
                        productDetails = recommendedProducts?.INVERTERS.find(
                          (inverter) => inverter.product.id === usp.id,
                        );
                        break;
                      case EViewSolarEstimationSolarProductCategory.BATTERIES:
                        productDetails = recommendedProducts?.BATTERIES.find(
                          (battery) => battery.product.id === usp.id,
                        );
                        break;
                      case EViewSolarEstimationSolarProductCategory.PANELS:
                        productDetails = recommendedProducts?.PANELS.find(
                          (panel) => panel.product.id === usp.id,
                        );
                        break;
                    }

                    if (!productDetails) return null;

                    const product = productDetails.product;
                    const count = usp.count;

                    return (
                      <Row key={`` + index + usp.id} className="mt-2">
                        <Col>
                          <ApprovedSolarComponent
                            product={product}
                            count={count}
                          />
                        </Col>
                      </Row>
                    );
                  })}
              </div>
            </Collapse>
          </>
        )}
        {(estimationType === ESolarSolutionType.BOTH ||
          estimationType === ESolarSolutionType.WATER_HEATING_SYSTEM) && (
          <>
            <Row className="mt-4">
              <Col className="text-secondary font-size-14 font-weight-500">
                Water Heater Equipments
              </Col>
              <Col className="col-auto">
                <MaterialIcon
                  icon={"keyboard_arrow_up"}
                  size={16}
                  color="var(--color-dropdown-more-icon)"
                  className={`${styles.iconToggle} ${
                    waterHeaterCollapsed ? styles.isCollapsed : ""
                  } cursor-pointer`}
                  onClick={() => {
                    setWaterHeaterCollapsed((ps) => !ps);
                  }}
                />
              </Col>
            </Row>
            <Collapse in={!waterHeaterCollapsed}>
              <div>
                {assembleSystemData &&
                  userSelectedProducts.map((usp, index) => {
                    let productDetails:
                      | IViewSolarEstimationSolarProductAndOtherDetails
                      | undefined;
                    const recommendedProducts =
                      assembleSystemData.recommendedProducts;

                    switch (usp.category) {
                      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
                        productDetails =
                          recommendedProducts?.WATER_HEATERS.find(
                            (heater) => heater.product.id === usp.id,
                          );
                        break;
                      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
                        productDetails =
                          recommendedProducts?.WATER_HEATER_CONTROLLERS.find(
                            (controller) => controller.product.id === usp.id,
                          );
                        break;
                    }

                    if (!productDetails) return null;

                    const product = productDetails.product;
                    const count = usp.count;

                    return (
                      <Row key={`` + index + usp.id} className="mt-2">
                        <Col>
                          <ApprovedSolarComponent
                            product={product}
                            count={count}
                          />
                        </Col>
                      </Row>
                    );
                  })}
              </div>
            </Collapse>
          </>
        )}
        <Row className="mt-4 px-4 align-items-center">
          <Col xl={6}>
            <Row className="align-items-center">
              <Col className="col-auto">
                <AppSwitch
                  isOn={includeOtherFees}
                  size="sm"
                  onSwitch={(value) => {
                    setIncludeOtherFees(value);
                  }}
                />
              </Col>
              <Col>
                <Row>
                  <Col className="text-label font-weight-500">
                    Do you want the installation to be done by Chloride Exide?
                  </Col>
                </Row>
                <Row className="mt-1">
                  <Col className="text-advance-description font-size-12">
                    If selected Solar system installation will be handled by
                    Chloride Exide experts & installation cost will be included
                    into the bill
                  </Col>
                </Row>
                <Row className="mt-1">
                  <Col>
                    <div className="text-brown font-size-12 bg-background-15 px-4 py-2 rounded-3">
                      Installation & Accessories cost subject to change based on
                      survey by experts. Shown values are approximate only
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col className="text-end">
            <Row>
              <Col className="text-advance-description font-size-14">
                Components Cost
              </Col>
              <Col xl={4} className="title-2 font-weight-500">
                {currency}&nbsp;
                {formatWithCommas(Number(Helper.roundTo2(componentCost)))}
              </Col>
            </Row>
            {includeOtherFees && (
              <Row>
                <Col className="text-advance-description font-size-14">
                  Approximate Installation Cost
                </Col>
                <Col xl={4} className="title-2 font-weight-500">
                  {currency}&nbsp;
                  {formatWithCommas(Number(Helper.roundTo2(installationCost)))}
                </Col>
              </Row>
            )}
            {includeOtherFees && (
              <Row>
                <Col className="text-advance-description font-size-14">
                  Approximate Accessories Cost
                </Col>
                <Col xl={4} className="title-2 font-weight-500">
                  {currency}&nbsp;
                  {formatWithCommas(Number(Helper.roundTo2(accessoriesCost)))}
                </Col>
              </Row>
            )}
            <Row className="mt-2">
              <Col className="text-advance-description">
                {includeOtherFees ? "Approximate" : ""} Total
              </Col>
              <Col xl={4}>
                <Row>
                  <Col className="title-2 font-size-20 font-weight-600">
                    {currency}&nbsp;
                    {formatWithCommas(Number(Helper.roundTo2(totalCost)))}
                  </Col>
                </Row>
                <Row className="text-advance-description font-weight-400 font-size-12">
                  <Col>Tax included</Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-5 align-items-center justify-content-between">
          <Col className="col-auto">
            <button
              className={`bg-background-6 border-0 rounded-3 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer`}
              onClick={() => setIsShowUpdateHistoryModal(true)}
            >
              <Row className="align-items-center">
                <Col className="col-auto pe-0">
                  <div className={styles.updateHistoryIconWrapper}>
                    <MaterialIcon
                      size={16}
                      icon="receipt_long"
                      color="var(--color-receipt-icon)"
                      className="bg-background-5 p-1 rounded-circle"
                    />
                    {comment && (
                      <div className={styles.updateHistoryCount}>1</div>
                    )}
                  </div>
                </Col>
                <Col className="title-4 font-weight-500 font-size-13">
                  Update History
                </Col>
              </Row>
            </button>
          </Col>
          <Col className="col-auto">
            <Row className="align-items-center">
              <Col className="col-auto">
                <button
                  className={`bg-background-16 border-0 rounded-2 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer ${styles.button}`}
                  onClick={onSave}
                >
                  <Row className="align-items-center">
                    <Col className="col-auto pe-0">
                      <MaterialIcon size={16} icon="save" color="#384F82" />
                    </Col>
                    <Col className="text-primary font-weight-600 font-size-16">
                      Save
                    </Col>
                  </Row>
                </button>
              </Col>
              <Col className="col-auto">
                <button
                  className={`bg-background-16 border-0 rounded-2 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer ${styles.button}`}
                  onClick={() => {
                    setIsShowSubmitForExpertApprovalModal(true);
                  }}
                >
                  <Row className="align-items-center">
                    <Col className="col-auto pe-0">
                      <MaterialIcon size={16} icon="ungroup" color="#384F82" />
                    </Col>
                    <Col className="text-primary font-weight-600 font-size-16">
                      Submit for Expert Approval
                    </Col>
                  </Row>
                </button>
              </Col>
              <Col className="col-auto">
                <AppButton
                  text="Purchase System"
                  iconName="shopping_cart"
                  iconColor="#fff"
                  className="px-4"
                  style={{ height: "40px", borderRadius: "8px" }}
                  // disabled={includeOtherFees}
                  disabled={true}
                  onClick={() => {
                    if (!auth.token) {
                      setIsShowSystemPurchaseModal(true);
                      return;
                    }

                    if (estimatorDetails.estimationId) {
                      onPurchaseSystem(estimatorDetails.estimationId);
                    }
                  }}
                  isLoading={isLoadingPurchaseSolarEstimation}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <UpdateHistoryModal
        show={isShowUpdateHistoryModal}
        onClose={() => setIsShowUpdateHistoryModal(false)}
        estimationId={estimatorDetails.estimationId}
        estimatorToken={estimatorDetails.estimationToken}
        currency={currency}
        estimationNo={estimationNo}
      />
      <SubmitForApprovalLoginModal
        show={isShowSubmitForApprovalLoginModal}
        onClose={() => {
          setIsShowSubmitForApprovalLoginModal(false);
        }}
        onCancel={() => {
          setIsShowSubmitForApprovalLoginModal(false);
        }}
        onConfirm={() => {
          let solarEstimationType: EEstimationType;
          if (pathname.includes("advanced")) {
            solarEstimationType = EEstimationType.ADVANCED;
          } else if (pathname.includes("simplified")) {
            solarEstimationType = EEstimationType.SIMPLIFIED;
          } else if (pathname.includes("direct")) {
            solarEstimationType = EEstimationType.DIRECT;
          } else {
            solarEstimationType = EEstimationType.WATER_HEATER;
          }

          const dataToSubmitForApproval: IDataToSubmitForApproval = {
            estimatorDetails,
            title,
            estimationType,
            applicationType,
            utilityOptionType,
            solarEstimationType,
            systemType,
            inverterPhaseType,
            country,
            monthlyRequiredEnergy,
            maxRequiredPower,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            estimationSpaceClusterId,
            devices,
            address,
            note,
            includeOtherFees,
            longitude,
            latitude,
          };

          localStorage.setItem(
            "dataToSubmitForApproval",
            JSON.stringify(dataToSubmitForApproval),
          );
          setIsShowSubmitForApprovalLoginModal(false);
          navigate(AppRoute.LOGIN);
        }}
      />
      <SubmitForExpertApprovalModal
        show={isShowSubmitForExpertApprovalModal}
        onCancel={() => {
          setIsShowSubmitForExpertApprovalModal(false);
        }}
        onClose={() => {
          setIsShowSubmitForExpertApprovalModal(false);
        }}
        onConfirm={(
          address: string,
          longitude: number,
          latitude: number,
          note: string,
        ) => {
          if (!auth.token) {
            setLongitude(longitude);
            setLatitude(latitude);
            setAddress(address);
            setNote(note);
            setIsShowSubmitForExpertApprovalModal(false);
            setIsShowSubmitForApprovalLoginModal(true);
            return;
          }

          setIsShowSubmitForExpertApprovalModal(false);
          onSubmitForApproval(address, longitude, latitude, note);
        }}
        includeOtherFees={includeOtherFees}
      />
      <SystemPurchaseModal
        show={isShowSystemPurchaseModal}
        onCancel={() => setIsShowSystemPurchaseModal(false)}
        onClose={() => setIsShowSystemPurchaseModal(false)}
        onConfirm={() => {
          let solarEstimationType: EEstimationType;
          if (pathname.includes("advanced")) {
            solarEstimationType = EEstimationType.ADVANCED;
          } else if (pathname.includes("simplified")) {
            solarEstimationType = EEstimationType.SIMPLIFIED;
          } else if (pathname.includes("direct")) {
            solarEstimationType = EEstimationType.DIRECT;
          } else {
            solarEstimationType = EEstimationType.WATER_HEATER;
          }

          const dataToSubmitForApproval: IDataToSubmitForApproval = {
            estimatorDetails,
            title,
            estimationType,
            applicationType,
            utilityOptionType,
            solarEstimationType,
            systemType,
            inverterPhaseType,
            country,
            monthlyRequiredEnergy,
            maxRequiredPower,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            estimationSpaceClusterId,
            devices,
            address,
            note,
            includeOtherFees,
            longitude,
            latitude,
          };

          localStorage.setItem(
            "dataToSubmitForApproval",
            JSON.stringify(dataToSubmitForApproval),
          );
          localStorage.setItem("isPurchase", "true");
          setIsShowSubmitForApprovalLoginModal(false);
          navigate(AppRoute.LOGIN);
        }}
      />
      <SpinnerModal
        show={isSubmittingForApproval || isLoadingCalculateSolarEstimationCost}
      />
    </>
  );
};

export default ApprovedSystem;
